import type {
  EntityPreview,
  MatchpropMode,
  PropertyValueMap
} from "@dxf-ai-editor/contracts";

import {
  getPropertiesForChannel,
  isCommonObjectProperty,
  resolveMatchpropChannels,
  type PropertyChannel
} from "./propertyCapabilities";
import { applySimpleGeometryPropertyChanges, applyExtendedEntityPropertyChanges } from "./simpleGeometry";

export type PropertyChange = {
  name: string;
  value: string;
};

export type MutationSkip = {
  entityId: string;
  property: string;
  reason: string;
  /**
   * fix/properties-apply-derived-fields-skip — distinguishes a KNOWN
   * computed/display-only field (never independently settable by design —
   * AutoCAD shows it read-only, e.g. Line Length/Angle) from a GENUINE
   * incompatibility (the property does not apply to this entity type, or
   * fails a real validation rule). Only "incompatible" skips count toward
   * the "skipped N incompatible property application(s)" audit summary
   * (`summarizePropertyMutation` below) — a derived field riding along
   * unedited in a full Properties-panel draft submit is not a failed write.
   */
  kind: "derived" | "incompatible";
};

export type PropertyMutationResult = {
  entities: EntityPreview[];
  updatedEntityIds: string[];
  appliedChanges: PropertyChange[];
  skipped: MutationSkip[];
  summary: string;
};

type BaseMutationInput = {
  entities: EntityPreview[];
  targetEntityIds: string[];
  changes: PropertyChange[];
};

/**
 * ONE-TRUTH per-target property gate for a property write through a channel.
 * The SHELL property path (`setObjectProperties` / `setEntitySpecificProperties`)
 * routes EVERY common + entity-specific change through this predicate, so a
 * change is applied to a target ONLY when:
 *
 *   1. the property is in the target type's CHANNEL CAPABILITY list
 *      (`getPropertiesForChannel(type, channel)` — per-type override table), AND
 *   2. the property is EXPOSED on the target — either declared editable
 *      (`entity.editableProperties`) OR already present in the target's
 *      property bag (`name in entity.properties`).
 *
 * Codex P2-1 (PR #403) — EXPORTED so the geometry-document translator
 * (`propertyTranslator.mergeCommonFieldsForType`) gates its writes by the SAME
 * predicate. A document write that only checked the capability list (1) but
 * skipped the exposure check (2) wrote an OPTIONAL common field (e.g. `Linetype
 * Scale`, absent on a default entity → not in `properties`, not editable) the
 * shell SKIPS → the geometry document / DXF export diverged from the shell.
 */
export function canApplyProperty(
  entity: EntityPreview,
  propertyName: string,
  channel: PropertyChannel
): boolean {
  const channelProperties = getPropertiesForChannel(entity.type, channel);
  const entityCanExposeProperty =
    entity.editableProperties.includes(propertyName) || propertyName in entity.properties;

  return channelProperties.includes(propertyName) && entityCanExposeProperty;
}

/**
 * ONE-TRUTH per-entity property-bag applier under a `canApplyProperty` gate —
 * shared by `applyChangesByChannel` (COMMON chprop / matchprop channels) and
 * the generic entity-specific tail of `setEntitySpecificProperties` (reached
 * by an entity kind absent from both the dedicated Line/Circle/Arc and
 * extended-kind branches). Both call sites were near-identical inline loops
 * (jscpd flagged the drift-risk) differing only in the channel and the
 * skip-reason wording, so this is the single body they now share.
 *
 * Every skip here is "incompatible" (unconditionally): this gate covers
 * COMMON object properties and the not-yet-dedicated-branch tail — the
 * derived-geometry-field confusion `simpleGeometry.ts` /
 * `extendedPropertyMutations.ts` guard against
 * (fix/properties-apply-derived-fields-skip) does not reach here today (no
 * common property, and no currently-dedicated-branch-absent kind, exposes a
 * `read-only-calc` typed descriptor through either channel).
 */
function applyChangesToEntityBag(
  entity: EntityPreview,
  changes: PropertyChange[],
  channel: PropertyChannel,
  skipReason: (change: PropertyChange) => string,
  appliedChanges: PropertyChange[],
  skipped: MutationSkip[],
  updatedEntityIds: Set<string>
): EntityPreview {
  let entityChanged = false;
  const nextProperties = { ...entity.properties };

  for (const change of changes) {
    if (!canApplyProperty(entity, change.name, channel)) {
      skipped.push({
        entityId: entity.id,
        property: change.name,
        reason: skipReason(change),
        kind: "incompatible"
      });
      continue;
    }

    if (nextProperties[change.name] === change.value) {
      continue;
    }

    nextProperties[change.name] = change.value;
    appliedChanges.push(change);
    entityChanged = true;
  }

  if (!entityChanged) {
    return entity;
  }

  updatedEntityIds.add(entity.id);
  return {
    ...entity,
    properties: nextProperties
  };
}

function applyChangesByChannel(
  input: BaseMutationInput,
  channel: PropertyChannel,
  summaryPrefix: string
): PropertyMutationResult {
  const targetIdSet = new Set(input.targetEntityIds);
  const updatedEntityIds = new Set<string>();
  const appliedChanges: PropertyChange[] = [];
  const skipped: MutationSkip[] = [];

  const entities = input.entities.map((entity) => {
    if (!targetIdSet.has(entity.id)) {
      return entity;
    }

    return applyChangesToEntityBag(
      entity,
      input.changes,
      channel,
      (change) => `${change.name} is not available in ${channel} for ${entity.type}`,
      appliedChanges,
      skipped,
      updatedEntityIds
    );
  });

  const updatedCount = updatedEntityIds.size;
  const summary =
    updatedCount > 0
      ? `${summaryPrefix}: ${updatedCount} object(s) updated`
      : `${summaryPrefix}: no applicable property changes`;

  return {
    entities,
    updatedEntityIds: [...updatedEntityIds],
    appliedChanges,
    skipped,
    summary
  };
}

export function setObjectProperties(input: BaseMutationInput): PropertyMutationResult {
  const filteredChanges = input.changes.filter((change) => isCommonObjectProperty(change.name));

  return applyChangesByChannel(
    {
      ...input,
      changes: filteredChanges
    },
    "chprop",
    "Object properties applied"
  );
}

/**
 * ONE-TRUTH merge of a per-entity-type applier's result (the simple-geometry
 * Line/Circle/Arc applier or the extended-kind applier) into the running
 * accumulators. Both call sites in `setEntitySpecificProperties` were
 * near-identical inline blocks (jscpd flagged the drift-risk) — differing
 * only in which applier they called. `result.skipped` is already
 * `MutationSkip[]`-shaped (`SimpleGeometryPropertySkip` and `MutationSkip`
 * share the exact same fields, including `kind` —
 * fix/properties-apply-derived-fields-skip), so no per-field remap is
 * needed; it flows straight through.
 */
function mergeApplierResult(
  result: {
    entity: EntityPreview;
    patch: unknown;
    appliedChanges: PropertyChange[];
    skipped: MutationSkip[];
  },
  appliedChanges: PropertyChange[],
  skipped: MutationSkip[],
  updatedEntityIds: Set<string>
): EntityPreview {
  appliedChanges.push(...result.appliedChanges);
  skipped.push(...result.skipped);

  if (result.patch) {
    updatedEntityIds.add(result.entity.id);
  }

  return result.entity;
}

export function setEntitySpecificProperties(input: BaseMutationInput): PropertyMutationResult {
  const filteredChanges = input.changes.filter((change) => !isCommonObjectProperty(change.name));
  const targetIdSet = new Set(input.targetEntityIds);
  const updatedEntityIds = new Set<string>();
  const appliedChanges: PropertyChange[] = [];
  const skipped: MutationSkip[] = [];

  const entities = input.entities.map((entity) => {
    if (!targetIdSet.has(entity.id)) {
      return entity;
    }

    if (entity.type === "Line" || entity.type === "Circle" || entity.type === "Arc") {
      const result = applySimpleGeometryPropertyChanges(entity, filteredChanges);
      return mergeApplierResult(result, appliedChanges, skipped, updatedEntityIds);
    }

    /* Extended entity types with dedicated geometry handling.
       S15 Slice B — Point + Ellipse route HERE (not the Line/Circle/Arc
       simple-geometry applier and not the generic tail below): their
       geometry labels are typed-writer-owned (geometryReducer Step 1d), so
       the shell write must run the typed descriptor mirror
       (`typedExtendedGeometryShellRejects` inside
       `applyExtendedEntityPropertyChanges`) — the bag may never keep a value
       the typed document refused (Ratio ∉ (0,1], non-finite coords, …).
       Before Slice B both kinds fell to the generic tail, whose
       `canApplyProperty(…, "properties")` gate skipped every geometry label
       (editableInProperties was empty) — the rows looked editable but
       silently no-opped. */
    if (
      entity.type === "Point" ||
      entity.type === "Ellipse" ||
      entity.type === "LWPOLYLINE" ||
      entity.type === "Spline" ||
      entity.type === "Text" ||
      entity.type === "MText" ||
      entity.type === "Dimension" ||
      entity.type === "Multileader" ||
      entity.type === "Revision Cloud" ||
      entity.type === "Hatch" ||
      entity.type === "Block" ||
      entity.type === "Rectangular Associative Array" ||
      entity.type === "Viewport" ||
      /* @codex F1 (P1) — legacy heavy POLYLINE / ACAD_TABLE / OLE2FRAME route
         HERE for the same reason Point/Ellipse do: their writable rows (the
         polyline group-70 bits + default widths + elevation, the table insertion
         point, the OLE frame corners) are typed-writer-owned (geometryReducer
         Step 1d), so the shell write must run the typed descriptor mirror and the
         read-only rows must skip as `derived`. Before this they fell to the
         generic tail below, whose `canApplyProperty(…, "properties")` gate PASSED
         (the capability roster lists every advertised row) — so the bag kept an
         edit the typed document never received. */
      entity.type === "Polyline" ||
      entity.type === "Table" ||
      entity.type === "OLE Frame"
    ) {
      const result = applyExtendedEntityPropertyChanges(entity, filteredChanges);
      return mergeApplierResult(result, appliedChanges, skipped, updatedEntityIds);
    }

    // Generic tail — reached only by an entity kind absent from BOTH
    // explicit branches above (currently none with a live derived-field row;
    // see `applyChangesToEntityBag`'s doc comment for why this stays
    // "incompatible" unconditionally).
    return applyChangesToEntityBag(
      entity,
      filteredChanges,
      "properties",
      (change) => `${change.name} is not available in properties for ${entity.type}`,
      appliedChanges,
      skipped,
      updatedEntityIds
    );
  });

  const updatedCount = updatedEntityIds.size;
  const summary =
    updatedCount > 0
      ? `Entity-specific properties applied: ${updatedCount} object(s) updated`
      : "Entity-specific properties applied: no applicable property changes";

  return {
    entities,
    updatedEntityIds: [...updatedEntityIds],
    appliedChanges,
    skipped,
    summary
  };
}

export function matchProperties(input: {
  entities: EntityPreview[];
  sourceEntityId: string;
  targetEntityIds: string[];
  mode: MatchpropMode;
}): PropertyMutationResult {
  const sourceEntity = input.entities.find((entity) => entity.id === input.sourceEntityId);

  if (!sourceEntity) {
    return {
      entities: input.entities,
      updatedEntityIds: [],
      appliedChanges: [],
      skipped: [],
      summary: "MATCHPROP: source entity not found"
    };
  }

  const changeNames = new Set<string>();

  for (const channel of resolveMatchpropChannels(input.mode)) {
    for (const propertyName of getPropertiesForChannel(sourceEntity.type, channel)) {
      if (propertyName in sourceEntity.properties) {
        changeNames.add(propertyName);
      }
    }
  }

  const changes = [...changeNames].map((propertyName) => ({
    name: propertyName,
    value: sourceEntity.properties[propertyName] ?? ""
  }));

  const targetEntityIds = input.targetEntityIds.filter((entityId) => entityId !== input.sourceEntityId);
  const channels = resolveMatchpropChannels(input.mode);

  return channels.reduce<PropertyMutationResult>(
    (result, channel) => {
      const channelResult = applyChangesByChannel(
        {
          entities: result.entities,
          targetEntityIds,
          changes
        },
        channel,
        `MATCHPROP ${input.mode}`
      );

      return {
        entities: channelResult.entities,
        updatedEntityIds: [...new Set([...result.updatedEntityIds, ...channelResult.updatedEntityIds])],
        appliedChanges: [...result.appliedChanges, ...channelResult.appliedChanges],
        skipped: [...result.skipped, ...channelResult.skipped],
        summary: channelResult.summary
      };
    },
    {
      entities: input.entities,
      updatedEntityIds: [],
      appliedChanges: [],
      skipped: [],
      summary: `MATCHPROP ${input.mode}: no applicable property changes`
    }
  );
}

export function mergePropertyValueMap(values: PropertyValueMap): PropertyChange[] {
  return Object.entries(values).map(([name, value]) => ({
    name,
    value
  }));
}
